"use client";

import type { FeatherySDK } from "@/app/_components/feathery/FeatheryContext";
import type { IconicVoice } from "@mw/shared/payload-types";

import { FeatheryForm } from "@/app/_components/feathery/FeatheryForm";
import { useAudio } from "@/app/_providers/AudioProvider";
import { toast } from "@/app/_providers/toast";
import { Button } from "@ui/components/core/Button";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { formatLink, getAudioUrl, getImageUrl } from "@utilities/payload";
import { ChevronLeft } from "lucide-react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Dialog, DialogContent } from "./Dialog";
import { DottedBorder } from "./GradientBorder";
import { BaseLink } from "./Link";

interface RequestVoiceDialogProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  voice?: IconicVoice | null;
}

// Component for requesting license for an existing voice
interface RequestExistingVoiceContentProps {
  voice: IconicVoice;
  onFormSubmit: () => void;
}

function RequestExistingVoiceContent({ voice, onFormSubmit }: RequestExistingVoiceContentProps) {
  const { playingId, play, stop } = useAudio();
  const t = useTranslations("ClientKeys.RequestVoiceDialog");

  const isPlaying = playingId === voice.id;
  const previewUrl = getAudioUrl(voice.previewAudio);
  const imageUrl = getImageUrl(voice.image);
  const agencyLinkFormatted = formatLink(voice.agencyLink);

  const handlePlayPause = () => {
    if (!previewUrl) return;

    if (isPlaying) {
      stop();
    } else {
      play(voice.id, previewUrl);
    }
  };

  const handleFormLoad = (sdk: FeatherySDK) => {
    sdk.setFieldValues({
      iconic_voices_voice_name: voice.name,
    });
  };

  return (
    <>
      {/* Left Sidebar */}
      <div className='flex basis-2/5 flex-col bg-overlay-lighter p-32 backdrop-blur-xl md:overflow-auto'>
        {/* Voice Image */}
        {imageUrl && (
          <div className='relative mb-24 aspect-1x1 w-full shrink-0 overflow-hidden rounded-lg bg-darker'>
            <Image alt={voice.name} className='object-cover' fill src={imageUrl} />
          </div>
        )}

        {/* Voice Name */}
        <h2 className='f-heading-03 mb-8'>{voice.name}</h2>

        {/* Short Bio */}
        {voice.shortBio && <p className='f-paragraph-01 mb-24 text-light'>{voice.shortBio}</p>}

        {/* Voice Details */}
        <div className='mb-32'>
          {voice.license && (
            <div className='mb-16 space-y-16'>
              <DottedBorder side='inline' />
              <div className='flex justify-between gap-16'>
                <dt className='f-paragraph-02 text-light'>{t("license")}</dt>
                <dd className='f-paragraph-02'>{voice.license}</dd>
              </div>
            </div>
          )}
          {voice.agencyName && (
            <div className='mb-16 space-y-16'>
              <DottedBorder side='inline' />
              <div className='flex justify-between gap-16'>
                <dt className='f-paragraph-02 text-light'>{t("agency_name")}</dt>
                <dd className='f-paragraph-02 text-right'>
                  {agencyLinkFormatted ? (
                    <BaseLink
                      className='underline'
                      href={agencyLinkFormatted.href}
                      rel={agencyLinkFormatted.rel}
                      target={agencyLinkFormatted.target}
                    >
                      {voice.agencyName}
                    </BaseLink>
                  ) : (
                    voice.agencyName
                  )}
                </dd>
              </div>
            </div>
          )}
        </div>

        {/* Preview Voice Button */}
        {previewUrl && (
          <div className='mt-auto'>
            <PlayPauseButton
              className='w-full'
              iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
              iconSize='sm'
              isPlaying={isPlaying}
              onClick={handlePlayPause}
              size='lg'
              variant='elevated'
            >
              {t("preview_voice")}
            </PlayPauseButton>
          </div>
        )}
      </div>

      {/* Right Form Section */}
      <div className='flex-1 bg-default p-32 md:overflow-y-auto'>
        <h3 className='f-heading-04 mb-4'>{t("request_license")}</h3>
        <p className='f-paragraph-02 mb-32 text-light'>{t("form_description", { voiceName: voice.name })}</p>
        <div className='min-h-[500px]'>
          <FeatheryForm formId='MCkkW3' fullWidth onLoad={handleFormLoad} onSubmit={onFormSubmit} />
        </div>
      </div>
    </>
  );
}

// Component for requesting a new voice (two-step flow)
interface RequestNewVoiceContentProps {
  onFormSubmit: () => void;
  onStepChange: (step: 1 | 2) => void;
}

function RequestNewVoiceContent({ onFormSubmit, onStepChange }: RequestNewVoiceContentProps) {
  const t = useTranslations("ClientKeys.RequestVoiceDialog");
  const [step, setStep] = useState<1 | 2>(1);
  const [voiceName, setVoiceName] = useState("");

  const handleNextStep = () => {
    if (voiceName.trim()) {
      setStep(2);
      onStepChange(2);
    }
  };

  const handleBackStep = () => {
    setStep(1);
    onStepChange(1);
  };

  const handleFormLoad = (sdk: FeatherySDK) => {
    sdk.setFieldValues({
      iconic_voices_voice_name: "[New voice] " + voiceName,
    });
  };

  return (
    <div className='flex-1 bg-default p-32 md:overflow-y-auto'>
      {step === 1 ? (
        // Step 1: Voice Name Input
        <div className='flex h-full flex-col'>
          <h3 className='f-heading-04 mb-4'>{t("request_new_voice")}</h3>
          <p className='f-paragraph-02 mb-32 text-light'>{t("enter_voice_name_description")}</p>

          <div className='mb-32'>
            <label className='f-paragraph-02 mb-12 block' htmlFor='voice-name'>
              {t("voice_name_label")}
            </label>
            <input
              className='border-light focus:border-primary f-paragraph-02 w-full rounded-lg border bg-none px-16 py-12 transition-colors placeholder:text-light focus:outline-none'
              id='voice-name'
              onChange={e => setVoiceName(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleNextStep();
                }
              }}
              placeholder={t("voice_name_placeholder")}
              type='text'
              value={voiceName}
            />
          </div>

          <div className='mt-auto'>
            <Button
              className='w-full'
              disabled={!voiceName.trim()}
              onClick={handleNextStep}
              size='lg'
              variant='elevated'
            >
              {t("continue")}
            </Button>
          </div>
        </div>
      ) : (
        // Step 2: Feathery Form
        <>
          <Button
            className='mb-24'
            icon={<ChevronLeft size={16} />}
            iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
            onClick={handleBackStep}
            size='md'
            variant='elevated'
          >
            {t("back")}
          </Button>
          <h3 className='f-heading-04 mb-4'>{t("request_license")}</h3>
          <p className='f-paragraph-02 mb-32 text-light'>{t("form_description", { voiceName })}</p>
          <div className='min-h-[500px]'>
            <FeatheryForm formId='MCkkW3' fullWidth onLoad={handleFormLoad} onSubmit={onFormSubmit} />
          </div>
        </>
      )}
    </div>
  );
}

// Main dialog component that renders appropriate content based on whether a voice is provided
export function RequestVoiceDialog({ onOpenChange, open, voice }: RequestVoiceDialogProps) {
  const t = useTranslations("ClientKeys.RequestVoiceDialog");
  const [newVoiceStep, setNewVoiceStep] = useState<1 | 2>(1);

  const handleFormSubmit = () => {
    toast.success(t("form_submitted"));
    onOpenChange(false);
  };

  const handleDialogClose = (newOpen: boolean) => {
    onOpenChange(newOpen);
    // Reset step when dialog closes
    if (!newOpen) {
      setNewVoiceStep(1);
    }
  };

  // Determine dialog size based on mode and step
  // Smaller for step 1 of new voice request, larger for existing voice or step 2
  const isNewVoiceMode = !voice;
  const isCompactSize = isNewVoiceMode && newVoiceStep === 1;

  return (
    <Dialog onOpenChange={handleDialogClose} open={open}>
      <DialogContent
        className={cn(
          "h-[95vh] max-w-5xl overflow-hidden overflow-y-auto rounded-xl border bg-none p-0 shadow-xs transition-all duration-300 ease-in-out md:overflow-y-hidden",
          isCompactSize ? "max-h-[400px]" : "max-h-[800px]",
        )}
        closeButtonVariant='inside'
        overlayClassName='bg-overlay-dark backdrop-blur-none'
      >
        <div className='flex h-full flex-col md:flex-row'>
          {voice ? (
            <RequestExistingVoiceContent voice={voice} onFormSubmit={handleFormSubmit} />
          ) : (
            <RequestNewVoiceContent onFormSubmit={handleFormSubmit} onStepChange={setNewVoiceStep} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
